import React, { useCallback, useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../ReduxToolkit/Store';
import PipelineBoard from './PipelineBoard';
import { getBusinessDashboard } from '../../../../Services/CRMAdminServices/CRMDashboardService';

/*
  PIPELINE - Fintrack's own clients, stage by stage.

  Reads top to bottom the way the question gets asked. How many are at each
  step and what share of the step before survived it. Then the cards, so you
  can see who they are and move them. The model comparison sits at the bottom,
  because it is the slowest question of the three.
*/

type Step = { name: string; modelPct: number; expectedFrom100: number; actual: number; hasStage: boolean };

const INK = '#14161C', MUTED = '#5A6274', LINE = '#E1E5EC', BLUE = '#0B3CC1';
const RAMP = ['#0B3CC1', '#1552C8', '#1E86A8', '#1C9184', '#1B8452', '#16713C'];
const STEPS: Record<string, string[]> = {
    property: ['Enquiry', 'Property Strategy Session', 'Property Presentation', 'EOI', 'Unconditional'],
    /* Named after the stages that exist in the CRM. They were invented names until
       1 Oct 2026 - Proposal, Agreement Sent, Agreement Signed - against a board that
       has MB - Proposal Sent and MB - Proposal Signed, so three steps read zero forever. */
    mb: ['Enquiry', 'Discovery Call', 'Proposal Sent', 'Proposal Signed', 'Onboarding', 'Live'],
};

const card: React.CSSProperties = { background: '#fff', border: `1px solid ${LINE}`, borderRadius: 12, padding: '14px 16px' };
const h2: React.CSSProperties = { fontSize: 14, fontWeight: 700, color: INK, margin: '0 0 10px' };
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const PipelineView: React.FC<{ which?: 'property' | 'mb'; title?: string; tabs?: React.ReactNode }> =
({ which = 'property', title = 'Pipeline', tabs }) => {
    const { RoleID } = useSelector((state: RootState) => state.adminAuth);
    const isAdmin = Number(RoleID) === 1 || RoleID == null;
    const [steps, setSteps] = useState<Step[]>(
        STEPS[which].map(n => ({ name: n, modelPct: 0, expectedFrom100: 0, actual: 0, hasStage: false }))
    );

    const load = useCallback(async () => {
        try {
            const now = new Date();
            const r: any = await getBusinessDashboard(iso(new Date(now.getFullYear(), now.getMonth(), 1)), iso(now), 14);
            const body = (r?.data && r.data.data !== undefined ? r.data.data : r?.data) || null;
            const wanted = which === 'mb' ? 'MB Managed' : 'Fintrack Property';
            const p = body?.businesses?.find((b: any) => b.name === wanted);
            if (p?.funnel?.length) setSteps(p.funnel);
        } catch { /* the board below still works without the model */ }
    }, [which]);

    useEffect(() => { load(); }, [load]);

    if (!isAdmin) {
        return <div style={{ padding: 24 }}>The pipeline is not available for this account.</div>;
    }

    const gapOptions: any = {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
        colors: ['#C9CEDA', BLUE],
        plotOptions: { bar: { horizontal: true, barHeight: '70%', borderRadius: 3 } },
        dataLabels: { enabled: false },
        grid: { borderColor: '#EEF0F5', strokeDashArray: 4 },
        xaxis: { categories: steps.map(s => s.name) },
        legend: { position: 'top', horizontalAlign: 'left', markers: { radius: 3 } },
        tooltip: { shared: true, intersect: false },
    };
    const gapSeries: any = [
        { name: 'Model expects', data: steps.map(s => s.expectedFrom100) },
        { name: 'Actual', data: steps.map(s => s.actual) },
    ];

    return (
        <div style={{ background: '#F5F6FA', minHeight: '100%', padding: '18px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 4px' }}>
                <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: INK }}>{title}</h1>
                {tabs}
            </div>
            <div style={{ fontSize: 12, color: MUTED, marginBottom: 14 }}>
                Conversion is this stage against the biggest stage before it, counted on everyone who ever reached each step.
            </div>

            {/*  There used to be a second row of chevrons above the board - the model's six
                steps - and it never agreed with the board underneath it, because the board
                draws the CRM's real stages and the model drew an idealised six. Two rows of
                arrows saying different things about the same pipeline is worse than one, so
                the counts and the conversion now sit on the board's own headers and there is
                one set of arrows.  */}
            <PipelineBoard which={which} />

            {/* The model is a property model. There is no MB benchmark worth drawing a
                broker against yet, so MB gets the board and nothing else. */}
            {which !== 'mb' && (
                <div style={{ ...card, marginTop: 18 }}>
                    <h2 style={h2}>Model against actual</h2>
                    <Chart options={gapOptions} series={gapSeries} type='bar' height={280} />
                </div>
            )}
        </div>
    );
};

const PipelinePage: React.FC<{ start?: 'property' | 'mb' }> = ({ start = 'property' }) => {
    //  16 Oct 2026: Pipeline is the Property business only; the MB pipeline is under
    //  Mortgage Brokers (/CRMPipelineMB). No tabs.
    return <PipelineView key={start} which={start} title={start === 'mb' ? 'MB Pipeline' : 'Pipeline'} />;
};

export default PipelinePage;
